/**
 * AI spam filter hooks — Vault-backed, account-scoped.
 *
 * Settings toggle + custom rules, the Report-Spam / Not-Spam message actions,
 * and whitelist/blacklist management. Mirrors useScreening.ts conventions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import type {
  MailboxScreenedSenderDto,
  MailboxSpamSettingsDto,
  MailboxSpamRetentionDto,
  EmailSpamSpendDto,
} from '@/lib/api';

const mb = queryKeys.email.mailbox;
const conn = queryKeys.email.connections;

export function useSpamSettings(accountId: string | undefined, enabled = true) {
  return useQuery<MailboxSpamSettingsDto>({
    queryKey: mb.spamSettings(accountId ?? ''),
    queryFn: () => api.getEmailMailboxSpamSettings(accountId as string),
    enabled: enabled && !!accountId,
  });
}

/** What the AI spam filter has cost this user (credits) by period — user-wide. */
export function useSpamSpend(enabled = true) {
  return useQuery<EmailSpamSpendDto>({
    queryKey: mb.spamSpend(),
    queryFn: () => api.getEmailMailboxSpamSpend(),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetSpamSettings(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      enabled?: boolean;
      rules?: string | null;
      spamRetention?: MailboxSpamRetentionDto;
    }) => api.setEmailMailboxSpamSettings(accountId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mb.spamSettings(accountId) });
      qc.invalidateQueries({ queryKey: conn.all });
    },
  });
}

export function useSpamSenders(
  accountId: string | undefined,
  disposition: 'approved' | 'rejected',
  enabled = true,
) {
  return useQuery<MailboxScreenedSenderDto[]>({
    queryKey: mb.spamSenderList(accountId ?? '', disposition),
    queryFn: async () =>
      (await api.listEmailMailboxSpamSenders(accountId as string, disposition)).senders,
    enabled: enabled && !!accountId,
  });
}

export function useDeleteSpamSender(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (screenedId: string) => api.deleteEmailMailboxSpamSender(screenedId, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.spamSenders(accountId) }),
  });
}

/**
 * Report the selected messages as spam: blacklist the sender(s) + file to Spam.
 * `accountId` is passed per-call so it works in the unified inbox (where a
 * message may belong to a different account than the active one).
 */
export function useReportSpam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, messageIds }: { accountId: string; messageIds: string[] }) =>
      api.reportEmailMailboxSpam(accountId, messageIds),
    onSuccess: (res, { accountId }) => {
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
      qc.invalidateQueries({ queryKey: mb.spamSenders(accountId) });
      toast({
        description: `Reported as spam — ${res.filed} message${res.filed === 1 ? '' : 's'} moved`,
      });
    },
  });
}

/** Mark a Spam message as not spam: whitelist the sender + restore to Inbox. */
export function useNotSpam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId }: { accountId: string; messageId: string }) =>
      api.notEmailMailboxSpam(messageId),
    onSuccess: (res, { accountId }) => {
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
      qc.invalidateQueries({ queryKey: mb.spamSenders(accountId) });
      toast({
        description: `Marked not spam — ${res.moved} message${res.moved === 1 ? '' : 's'} moved to Inbox`,
      });
    },
  });
}
